import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { T, useGT } from 'gt-react';
import { format } from 'date-fns';
import type { PricelistCaseListRow } from '@erp/rpc';
import { orpc } from '@/api/rpc/client';
import { pricelistVendorName } from '@/components/company/companyPickerDisplay';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/common/date-pickers/date-picker';
import { useToast } from 'src/context/ToastContext';
import {
  caseVendorsFromLegacy,
  formatPricelistCaseVendorNames,
} from './pricelistCaseVendors';
import {
  canPersistFilesReady,
  filesReadyDateValue,
  filesReadySaveDisabled,
} from './pricelistCaseFilesReady';
import { invalidatePricelistCaseQueries } from './pricelistCaseQueryInvalidation';

export function PricelistCaseFilesReadyDialog({
  pricelistCase,
  onOpenChange,
}: {
  pricelistCase: PricelistCaseListRow | null;
  onOpenChange: (open: boolean) => void;
}) {
  const gt = useGT();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const setFilesReady = useMutation(
    orpc.inventoryManagement.pricelistCases.setFilesReady.mutationOptions(),
  );
  const caseId = pricelistCase?.id;
  const [filesReadyAt, setFilesReadyAt] = useState<Date | undefined>(() =>
    filesReadyDateValue(pricelistCase?.filesReadyAt ?? null),
  );
  const dateInputValidRef = useRef(true);
  const [dateInputValid, setDateInputValid] = useState(true);

  function handleDateValidityChange(isValid: boolean) {
    dateInputValidRef.current = isValid;
    setDateInputValid(isValid);
  }

  async function persist(next: string | null) {
    if (
      !canPersistFilesReady({
        caseId,
        dateInputValid: dateInputValidRef.current,
      })
    ) {
      return;
    }
    if (caseId === undefined) return;
    try {
      await setFilesReady.mutateAsync({ caseId, filesReadyAt: next });
      invalidatePricelistCaseQueries(queryClient);
      showToast({
        title: gt('Upload reminder saved', {
          context: 'toast title after setting a pricelist files-ready date',
        }),
      });
      onOpenChange(false);
    } catch (error) {
      showToast({
        title: gt('Could not save upload reminder', {
          context:
            'toast title when setting a pricelist files-ready date fails',
        }),
        message: error instanceof Error ? error.message : undefined,
        color: 'error',
      });
    }
  }

  return (
    <Dialog open={pricelistCase !== null} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[440px]">
        <DialogHeader>
          <DialogTitle>
            <T context="dialog title, schedule when vendor price files will be ready">
              Schedule Upload Reminder
            </T>
          </DialogTitle>
          <DialogDescription className="line-clamp-1 break-words">
            {pricelistCase
              ? `${pricelistCase.caseNumber ?? ''} · ${formatPricelistCaseVendorNames(
                  caseVendorsFromLegacy(
                    pricelistCase.companyId,
                    pricelistCase.companyName,
                    pricelistCase.vendors,
                  ),
                  pricelistVendorName,
                )}`
              : null}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-1.5 py-1">
          <Label>
            <T context="field label, vendor-stated date the price file will be available">
              Files ready date
            </T>
          </Label>
          <DatePicker
            id="files-ready-date"
            value={filesReadyAt}
            onChange={setFilesReadyAt}
            onValidityChange={handleDateValidityChange}
            className="w-[160px]"
          />
        </div>
        <DialogFooter className="gap-2 sm:justify-between">
          <Button
            type="button"
            variant="ghost"
            disabled={
              setFilesReady.isPending || pricelistCase?.filesReadyAt === null
            }
            onClick={() => void persist(null)}
          >
            <T context="button, clear the files-ready upload reminder">Clear</T>
          </Button>
          <Button
            type="button"
            disabled={filesReadySaveDisabled(
              setFilesReady.isPending,
              dateInputValid,
            )}
            onClick={() =>
              void persist(
                filesReadyAt ? format(filesReadyAt, 'yyyy-MM-dd') : null,
              )
            }
          >
            <T context="button, save the files-ready upload reminder">Save</T>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
